// Cluster ZUWEISEN (B5) / Entscheidung E03 — der Abschluss einer Zuweisung
// OHNE Vorgangsanlage. Reine Ablauflogik, alle Wege als Deps → in Vitest ohne
// Netzwerk prüfbar.

import type { MailZuweisungErgebnis } from "./mailContactAssign";

export type AbschlussDeps = {
    /** POST /mail/links/kontakt — `mailLinkService.createForContact`. */
    verknuepfeMailMitKontakt: (contactId: number) => Promise<unknown>;
    /** Archivieren (Graph-Move) — `finishRead` aus dem Mail-Detail. */
    finishRead: () => Promise<boolean>;
};

export type AbschlussErgebnis = {
    verknuepft: boolean;
    archiviert: boolean;
    fehler?: string;
};

/**
 * Abschluss einer Zuweisung ohne Vorgang (B5). Die Reihenfolge ist tragend und
 * NICHT optional:
 *
 *   1. MailLink mit `contactId` schreiben (POST /mail/links/kontakt). Das ist
 *      der EINZIGE dauerhafte Effekt dieses Ausgangs — ohne ihn wäre „nur
 *      zuweisen" folgenlos, sobald die Adresse schon am Kontakt hängt oder das
 *      Schreibrecht fehlt.
 *   2. erst DANACH archivieren (E03). Der Graph-Move vergibt eine neue
 *      messageId; der Server zieht den vorher geschriebenen Link über
 *      `remapMessageId` nach. Ein danach geschriebener Link zeigte auf die tote Id.
 *
 * Scheitert Schritt 1, wird NICHT archiviert (Annahme A10): eine sichtbar
 * gescheiterte Zuweisung ist besser als eine archivierte Mail ohne Zuweisung.
 */
export async function schliesseZuweisungAb(
    ergebnis: MailZuweisungErgebnis,
    deps: AbschlussDeps,
): Promise<AbschlussErgebnis> {
    // Ausgang „Ohne Kontakt": es gibt nichts zu verknüpfen. Dieser Ausgang
    // führt ohnehin in den Vorgangs-Flow und archiviert dort über `onCreated`.
    if (!ergebnis.kontakt) {
        return { verknuepft: false, archiviert: false };
    }

    try {
        await deps.verknuepfeMailMitKontakt(ergebnis.kontakt.id);
    } catch (e) {
        const m = (e as { message?: unknown })?.message;
        return {
            verknuepft: false,
            archiviert: false,
            fehler:
                `Die Mail konnte dem Kontakt nicht zugewiesen werden: ` +
                `${typeof m === "string" && m.trim() ? m : "unbekannter Fehler"}. ` +
                `Sie wurde NICHT archiviert.`,
        };
    }

    const archiviert = await deps.finishRead();
    return { verknuepft: true, archiviert };
}
